import { useState } from 'react';
import { LogIn, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { StageFlowLogo } from './StageFlowLogo';
import { Button } from './ui/button';
import { useAuth } from '../data/AuthContext';
import { gaEvent } from '../data/ga4';

export function LoginPage() {
  const { login, loginWithGoogle, register, error, clearError, isLoading: authLoading } = useAuth();
  const [mode, setMode] = useState<'login' | 'register' | 'main'>('main');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Register fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await login(email, password);
      gaEvent('login', { method: 'password' });
    } catch {
      // error is set in context
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearError();
    setGoogleBusy(true);
    try {
      await loginWithGoogle();
      // On success the browser redirects to Google — leave the busy state on.
    } catch {
      // error is set in context
      setGoogleBusy(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await register({
        email: regEmail,
        password: regPassword,
        name: regName,
        role: 'member',
      });
      gaEvent('sign_up', { method: 'password' });
    } catch {
      // error is set in context
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          <p className="text-indigo-300 text-[14px]">Loading StageFlow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] p-4">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <StageFlowLogo className="w-12 h-12" />
        <div>
          <h1 className="text-[24px] tracking-tight text-white">StageFlow</h1>
          <p className="text-[13px] text-indigo-300">Theater Company Management</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-[13px] max-w-md w-full">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Sign-in Mode */}
      {mode === 'main' && (
        <div className="w-full max-w-md">
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
            <h2 className="text-[16px] text-white/90 mb-1 text-center">Sign in to StageFlow</h2>
            <p className="text-[13px] text-indigo-300 mb-5 text-center">
              Use your Google account — it links to your team profile automatically
            </p>

            <button
              onClick={handleGoogleSignIn}
              disabled={googleBusy || submitting}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-lg bg-white hover:bg-gray-100 text-gray-800 text-[14px] font-medium transition-colors disabled:opacity-60"
            >
              {googleBusy ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <GoogleGlyph />}
              {googleBusy ? 'Redirecting to Google...' : 'Continue with Google'}
            </button>

            <div className="border-t border-white/10 mt-5 pt-4 flex items-center justify-between">
              <button
                onClick={() => setMode('login')}
                className="text-[12px] text-indigo-300 hover:text-white transition-colors"
              >
                Sign in with email instead
              </button>
              <button
                onClick={() => setMode('register')}
                className="text-[12px] text-amber-300 hover:text-amber-200 transition-colors"
              >
                Create new account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Login Mode */}
      {mode === 'login' && (
        <div className="w-full max-w-sm">
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
            <h2 className="text-[16px] text-white/90 mb-5 text-center">Sign in with email</h2>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-[12px] text-indigo-300 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/50"
                  placeholder="you@stageflow.dev"
                />
              </div>

              <div>
                <label className="block text-[12px] text-indigo-300 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/50 pr-10"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                Sign In
              </Button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] uppercase tracking-wide text-indigo-300/70">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={googleBusy || submitting}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-lg bg-white hover:bg-gray-100 text-gray-800 text-[14px] font-medium transition-colors disabled:opacity-60"
            >
              {googleBusy ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <GoogleGlyph />}
              {googleBusy ? 'Redirecting to Google...' : 'Continue with Google'}
            </button>

            <button
              onClick={() => { setMode('main'); clearError(); }}
              className="mt-4 w-full text-center text-[12px] text-indigo-300 hover:text-white transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>
      )}

      {/* Register Mode */}
      {mode === 'register' && (
        <div className="w-full max-w-sm">
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
            <h2 className="text-[16px] text-white/90 mb-5 text-center">Create new account</h2>

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-[12px] text-indigo-300 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={regName}
                  onChange={e => setRegName(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/50"
                  placeholder="Alex Rodriguez"
                />
              </div>

              <div>
                <label className="block text-[12px] text-indigo-300 mb-1.5">Email</label>
                <input
                  type="email"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/50"
                  placeholder="alex@stageflow.dev"
                />
              </div>

              <div>
                <label className="block text-[12px] text-indigo-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={regPassword}
                  onChange={e => setRegPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/50"
                  placeholder="Min 6 characters"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Create Account & Sign In
              </Button>
            </form>

            <button
              onClick={() => { setMode('main'); clearError(); }}
              className="mt-4 w-full text-center text-[12px] text-indigo-300 hover:text-white transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>
      )}

      {submitting && (
        <div className="mt-4 flex items-center gap-2 text-[13px] text-indigo-300">
          <Loader2 className="w-4 h-4 animate-spin" />
          Setting up your session...
        </div>
      )}
    </div>
  );
}

// Google's four-color "G" — kept inline so the button has no asset dependency.
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className="w-4 h-4 shrink-0">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
